import { mapState } from 'pinia'

import { useEmojiStore } from 'src/stores/emoji.js'
import { useInstanceStore } from 'src/stores/instance.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  emojiTokens,
  pointAt,
  readText,
  renderTokens,
  textOffset,
} from 'src/services/rich_text/rich_text.js'

const instanceHost = (instance) => {
  try {
    return instance.instanceDomain
  } catch {
    return null
  }
}

// A known user for a mention, as the pill shows them. A local user may be
// written with this instance's host.
const mentionLookup = (users, instance) => (handle) => {
  const [name, host] = handle.split('@')
  let user = users.findUserByName(handle)
  if (!user && host?.toLowerCase() === instanceHost(instance)?.toLowerCase()) {
    user = users.findUserByName(name)
  }
  if (!user) return null
  return {
    // escaped for HTML in name; the pill sets text, not HTML
    name: user.name_unescaped || user.screen_name,
    avatar: user.profile_image_url,
    emoji: user.emoji,
  }
}

// Tokens for text as the user's settings show it: custom emoji always as
// pictures, unicode emoji when "emoji as pictures" is on, known users as
// pills (unless mentions is off).
export const useRichTokens = ({ mentions = true } = {}) => {
  const emoji = useEmojiStore()
  const config = useMergedConfigStore()
  const mention = mentions && mentionLookup(useUsersStore(), useInstanceStore())
  let cache = null
  // caret: a handle ending there is still being typed and stays text
  return (text, caret = -1) => {
    if (cache?.list !== emoji.customEmoji) {
      cache = {
        list: emoji.customEmoji,
        map: new Map(emoji.customEmoji.map((e) => [e.displayText, e.imageUrl])),
      }
    }
    return emojiTokens(text, {
      custom: cache.map,
      unicode: !!config.mergedConfig.emojiAsImages,
      mention,
      caret,
    })
  }
}

// Focuses a RichTextarea with the caret after its text; a contenteditable
// element would put it at the start.
export const focusAtEnd = (el) => {
  if (!el) return
  el.focus()
  el.setSelectionRange?.(el.value.length)
}

// A text field that shows emoji as pictures. Toward its users it behaves
// like a <textarea>: the element has value, selectionStart, selectionEnd
// and setSelectionRange, and fires input events. Every picture stands for
// its exact source text, so offsets are the same as in the plain text.
// singleLine makes it an <input> (the content warning): Enter adds no
// line, a pasted one becomes a space.
const RichTextarea = {
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    singleLine: { type: Boolean, default: false },
    // known users as pills; off where the server makes no mentions
    mentions: { type: Boolean, default: true },
  },
  emits: ['update:modelValue'],
  data: () => ({
    composing: false,
  }),
  computed: {
    ...mapState(useMergedConfigStore, {
      unicodeAsImages: (store) => !!store.mergedConfig.emojiAsImages,
    }),
    ...mapState(useEmojiStore, ['customEmoji']),
  },
  created() {
    this.tokensFor = useRichTokens({ mentions: this.mentions })
  },
  mounted() {
    const el = this.$el
    const self = this
    Object.defineProperties(el, {
      value: {
        configurable: true,
        get: () => readText(el),
        set: (text) => self.render(text ?? ''),
      },
      selectionStart: { configurable: true, get: () => self.selection()[0] },
      selectionEnd: { configurable: true, get: () => self.selection()[1] },
    })
    el.setSelectionRange = (start, end = start) => self.select(start, end)
    // for a copy of the field that must draw the same (EmojiInput's caret)
    el.tokensFor = this.tokensFor
    this.render(this.modelValue)
  },
  watch: {
    modelValue(text) {
      if (text !== readText(this.$el)) this.renderKeepingCaret(text)
    },
    // pictures follow the settings and the emoji list once it loads;
    // never in the middle of composing text
    unicodeAsImages() {
      if (!this.composing) this.renderKeepingCaret(this.modelValue)
    },
    customEmoji() {
      if (!this.composing) this.renderKeepingCaret(this.modelValue)
    },
  },
  methods: {
    render(text, caret = -1) {
      renderTokens(this.$el, this.tokensFor(text, caret))
    },
    focused() {
      return document.activeElement === this.$el
    },
    renderKeepingCaret(text) {
      const focused = this.focused()
      const [start, end] = this.selection()
      this.render(text, focused ? end : -1)
      if (focused) this.select(start, end)
    },
    // [start, end] as text offsets; the end of the text when the
    // selection is elsewhere
    selection() {
      const el = this.$el
      const sel = window.getSelection()
      if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
        const length = readText(el).length
        return [length, length]
      }
      const range = sel.getRangeAt(0)
      // an edge inside a pill (a click on its name) goes to its outside
      const outside = (node, offset, after) => {
        const holder =
          node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode
        const pill = holder.closest('[data-text]')
        if (!pill || !el.contains(pill)) return [node, offset]
        const index = [...pill.parentNode.childNodes].indexOf(pill)
        return [pill.parentNode, after ? index + 1 : index]
      }
      return [
        textOffset(
          el,
          ...outside(range.startContainer, range.startOffset, false),
        ),
        textOffset(el, ...outside(range.endContainer, range.endOffset, true)),
      ]
    },
    select(start, end = start) {
      const el = this.$el
      const from = pointAt(el, start)
      const to = pointAt(el, end)
      const range = document.createRange()
      range.setStart(from.node, from.offset)
      range.setEnd(to.node, to.offset)
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(range)
    },
    // Redraws when the pictures no longer match the text (a typed or
    // pasted shortcode, a skin tone added after a picture) or the browser
    // added something other than the lines it makes for Enter. Redrawing
    // resets undo, so it is left out whenever it is not needed.
    normalize() {
      const el = this.$el
      let text = readText(el)
      // a space for each line keeps the offsets, and the caret, in place
      const lines = this.singleLine && text.includes('\n')
      if (lines) text = text.replaceAll('\n', ' ')
      const caret = this.focused() ? this.selection()[1] : -1
      const wanted = this.tokensFor(text, caret)
        .filter((token) => token.type !== 'text')
        .map((token) => token.text)
      const shown = [...el.querySelectorAll('[data-text]')].map(
        (node) => node.dataset.text,
      )
      const end = el.querySelector('br[data-end]')
      // pictures, pills (not what is inside them) and the browser's lines
      const stray =
        end !== el.lastChild ||
        [...el.querySelectorAll('*')].some(
          (node) =>
            !('text' in node.dataset) &&
            !node.parentElement.closest('[data-text]') &&
            node.nodeName !== 'BR' &&
            node.nodeName !== 'DIV',
        )
      if (lines || stray || wanted.join('\u0000') !== shown.join('\u0000')) {
        this.renderKeepingCaret(text)
      }
      return text
    },
    // copy, cut and drag carry the text behind the pictures
    selectedText() {
      const [start, end] = this.selection()
      return readText(this.$el).slice(start, end)
    },
    onCopy(e) {
      const text = this.selectedText()
      if (!text) return
      e.clipboardData.setData('text/plain', text)
      e.preventDefault()
    },
    onCut(e) {
      this.onCopy(e)
      // the browser's own delete keeps undo working
      if (e.defaultPrevented) document.execCommand('delete')
    },
    onDragStart(e) {
      const text = this.selectedText()
      if (text) e.dataTransfer?.setData('text/plain', text)
    },
    onKeydown(e) {
      // an Enter that ends an IME composition is the IME's
      if (e.isComposing || e.keyCode === 229) return
      if (this.singleLine && e.key === 'Enter') e.preventDefault()
    },
    onInput() {
      if (this.composing) return
      this.$emit('update:modelValue', this.normalize())
    },
    // a handle left behind unfinished becomes a pill when the field is left
    onBlur() {
      this.normalize()
    },
    onCompositionEnd() {
      this.composing = false
      this.onInput()
    },
  },
}

export default RichTextarea
